// Tab 2 · Políticas Especiales: filtros + master-detail, CONECTADO a `/store-policies` de
// loan-offerings-business. Las tiendas y los productos del alcance salen del padrón real de retail.
import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Shield } from 'lucide-react';
import { toast } from 'sonner';
import { Button, ErrorState, Loading } from '@/kit';
import { userMessageOf } from '@/api/errors';
import { cn } from '@/lib/cn';
import { MasterDetail, MasterDetailEmpty } from '@/shared/patterns/MasterDetail';
import { useProductCatalog, useStoreCatalog } from '../../promotions/api/useScopeCatalog';
import type { SegmentCatalogState } from '../../segments/types';
import { toCreateRequest, toUpdateRequest } from '../api/storePolicyMappers';
import {
    STORE_POLICIES_KEY,
    useChangeStorePolicyStatus,
    useCreateStorePolicy,
    useStorePolicies,
    useStorePolicyLog,
    useUpdateStorePolicy,
} from '../api/useStorePolicies';
import {
    actionTargetStatus,
    countDraftChanges,
    derivePolicyType,
    draftFromPolicy,
    emptyDraft,
    policyView,
    readFailureCopy,
    storePolicyErrorCopy,
    validateDraft,
    validityLabel,
} from '../logic';
import { STORE_POLICIES_PERMISSIONS, type StorePolicyAbilities } from '../permissions';
import {
    POLICY_STATUS_LABEL,
    POLICY_TYPE_LABEL,
    type PolicyAction,
    type PolicyDraft,
    type PolicyStatus,
    type PolicyType,
    type Segment,
    type SpecialPolicy,
} from '../types';
import { PolicyDetail } from './PolicyDetail';
import { PolicyStatusDialog } from './PolicyStatusDialog';
import { StatusBadge, TypeBadge } from './ui';

const ANY = 'todos';

type SortCol = 'name' | 'type' | 'status' | 'validity';

const STATUS_OPTIONS: PolicyStatus[] = ['DRAFT', 'SCHEDULED', 'ACTIVE', 'PAUSED', 'EXPIRED'];
const TYPE_OPTIONS: PolicyType[] = [
    'suspend',
    'sci_min',
    'down_min',
    'tna',
    'max_amount',
    'allowed_skus',
    'combo',
];

/** Lo que hay abierto en el panel derecho. `policyId` en `null` = política nueva sin crear. */
interface EditorState {
    policyId: string | null;
    original: PolicyDraft;
    draft: PolicyDraft;
}

export function SpecialPoliciesTab({
    abilities,
    segments,
    segmentCatalogState,
    today,
}: {
    abilities: StorePolicyAbilities;
    /** El catálogo real del tenant, prestado de Segmentos. Vacío mientras el estado no sea `ready`. */
    segments: readonly Segment[];
    segmentCatalogState: SegmentCatalogState;
    today: string;
}) {
    const queryClient = useQueryClient();
    const { policies, isLoading, error } = useStorePolicies(abilities.canRead);
    const storeCatalog = useStoreCatalog();
    const productCatalog = useProductCatalog();

    const [fltStatus, setFltStatus] = useState<string>(ANY);
    const [fltType, setFltType] = useState<string>(ANY);
    // Orden alfabético por nombre: es el default de la casa para cualquier listado.
    const [sort, setSort] = useState<{ col: SortCol; dir: 'asc' | 'desc' }>({
        col: 'name',
        dir: 'asc',
    });
    const [editor, setEditor] = useState<EditorState | null>(null);
    const [openAction, setOpenAction] = useState<PolicyAction | null>(null);

    const createMutation = useCreateStorePolicy();
    const updateMutation = useUpdateStorePolicy();
    const statusMutation = useChangeStorePolicyStatus();

    const selected: SpecialPolicy | null = useMemo(() => {
        if (!editor?.policyId) return null;
        return policies.find((item) => item.id === editor.policyId) ?? null;
    }, [editor, policies]);

    const log = useStorePolicyLog(editor?.policyId ?? null);

    const visible = useMemo(() => {
        const filtered = policies.filter((policy) => {
            if (fltStatus !== ANY && policyView(policy) !== fltStatus) return false;
            if (fltType !== ANY && derivePolicyType(policy) !== fltType) return false;
            return true;
        });
        const value = (policy: SpecialPolicy): string => {
            switch (sort.col) {
                case 'name':
                    return policy.name;
                case 'type':
                    return POLICY_TYPE_LABEL[derivePolicyType(policy)];
                case 'status':
                    return POLICY_STATUS_LABEL[policyView(policy)];
                case 'validity':
                    return policy.validFrom ?? '';
            }
        };
        return [...filtered].sort(
            (a, b) => value(a).localeCompare(value(b), 'es-MX') * (sort.dir === 'asc' ? 1 : -1),
        );
    }, [policies, fltStatus, fltType, sort]);

    const changeCount = editor ? countDraftChanges(editor.original, editor.draft) : 0;

    const openPolicy = useCallback(
        (policyId: string) => {
            const policy = policies.find((item) => item.id === policyId);
            if (!policy) return;
            const draft = draftFromPolicy(policy);
            setEditor({ policyId, original: draft, draft });
        },
        [policies],
    );

    const startNewPolicy = useCallback(() => {
        const draft = emptyDraft(today);
        setEditor({ policyId: null, original: draft, draft });
    }, [today]);

    const discard = useCallback(() => {
        setEditor((current) => {
            if (!current) return null;
            // Descartar una política nueva cierra el panel: no hay original al que volver.
            if (current.policyId === null) return null;
            return { ...current, draft: current.original };
        });
    }, []);

    const save = useCallback(() => {
        if (!editor) return;
        const problems = validateDraft(editor.draft);
        if (problems.length > 0) {
            toast.error('Faltan datos para guardar', { description: problems[0] });
            return;
        }
        if (editor.policyId === null) {
            createMutation.mutate(toCreateRequest(editor.draft), {
                onSuccess: (created) => {
                    toast.success('Política creada', {
                        description: 'Queda en borrador; publícala cuando esté lista.',
                    });
                    const policyId = created.policyId;
                    if (policyId === undefined) {
                        setEditor(null);
                        return;
                    }
                    setEditor({ policyId, original: editor.draft, draft: editor.draft });
                },
                onError: (mutationError) => {
                    toast.error('No se pudo crear la política', {
                        description: storePolicyErrorCopy(mutationError),
                    });
                },
            });
            return;
        }
        const patch = toUpdateRequest(editor.original, editor.draft);
        updateMutation.mutate(
            { policyId: editor.policyId, patch },
            {
                onSuccess: () => {
                    toast.success('Cambios guardados');
                    setEditor((current) =>
                        current ? { ...current, original: current.draft } : null,
                    );
                },
                onError: (mutationError) => {
                    toast.error('No se pudieron guardar los cambios', {
                        description: storePolicyErrorCopy(mutationError),
                    });
                },
            },
        );
    }, [createMutation, editor, updateMutation]);

    const confirmAction = useCallback(
        (reason?: string) => {
            if (!openAction || !selected) return;
            const action = openAction;
            statusMutation.mutate(
                { policyId: selected.id, status: actionTargetStatus(action), reason },
                {
                    onSuccess: () => {
                        toast.success('Listo', { description: selected.name });
                        setEditor((current) =>
                            current ? { ...current, original: current.draft } : null,
                        );
                    },
                    onError: (mutationError) => {
                        toast.error('No se pudo cambiar el estado', {
                            description: userMessageOf(mutationError),
                        });
                    },
                },
            );
            setOpenAction(null);
        },
        [openAction, selected, statusMutation],
    );

    if (!abilities.canRead) {
        return (
            <div role="alert" className="p-6 text-[12.5px] text-muted-foreground">
                No tienes permiso para ver las políticas especiales. Si crees que es un error,
                contacta al administrador.
            </div>
        );
    }

    if (isLoading) {
        return <Loading text="Cargando políticas especiales…" />;
    }

    if (error) {
        // Un 403 no es una caída: se nombra el permiso que falta y NO se ofrece reintentar.
        const copy = readFailureCopy(
            error,
            'las políticas especiales',
            STORE_POLICIES_PERMISSIONS.read,
        );
        return (
            <ErrorState
                className="m-6"
                title={copy.title}
                description={copy.description}
                onRetry={
                    copy.canRetry
                        ? () => {
                              void queryClient.invalidateQueries({
                                  queryKey: [STORE_POLICIES_KEY, 'list'],
                              });
                          }
                        : undefined
                }
            />
        );
    }

    const toggleSort = (col: SortCol): void => {
        setSort((s) =>
            s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' },
        );
    };

    const th = (col: SortCol, label: string): React.ReactNode => (
        <th
            onClick={() => {
                toggleSort(col);
            }}
            className="cursor-pointer select-none bg-muted/40 px-2.5 py-1.5 text-left text-[9px] font-bold uppercase tracking-wide text-muted-foreground hover:text-foreground"
        >
            {label} {sort.col === col ? (sort.dir === 'asc' ? '▲' : '▼') : ''}
        </th>
    );

    const filterSelect =
        'cursor-pointer rounded border border-border bg-card px-2 py-[3px] text-[11.5px] text-foreground outline-none focus:border-ring';

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <MasterDetail
                list={
                    <>
                        <div className="flex flex-none flex-wrap items-center gap-3 border-b border-border bg-muted/30 px-3 py-2.5">
                            <label className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                                Estado
                                <select
                                    value={fltStatus}
                                    onChange={(e) => {
                                        setFltStatus(e.target.value);
                                    }}
                                    className={filterSelect}
                                >
                                    <option value={ANY}>Todos</option>
                                    {STATUS_OPTIONS.map((status) => (
                                        <option key={status} value={status}>
                                            {POLICY_STATUS_LABEL[status]}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                                Tipo
                                <select
                                    value={fltType}
                                    onChange={(e) => {
                                        setFltType(e.target.value);
                                    }}
                                    className={filterSelect}
                                >
                                    <option value={ANY}>Todos</option>
                                    {TYPE_OPTIONS.map((type) => (
                                        <option key={type} value={type}>
                                            {POLICY_TYPE_LABEL[type]}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            {abilities.canCreate ? (
                                <Button
                                    size="sm"
                                    className="ml-auto h-6 px-2.5 text-[11px]"
                                    onClick={startNewPolicy}
                                >
                                    + Nueva política
                                </Button>
                            ) : null}
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto">
                            <table className="w-full border-collapse text-[11px]">
                                <thead>
                                    <tr>
                                        {th('name', 'Política')}
                                        {th('type', 'Tipo')}
                                        {th('status', 'Estado')}
                                        {th('validity', 'Vigencia')}
                                    </tr>
                                </thead>
                                <tbody>
                                    {visible.length === 0 ? (
                                        <tr>
                                            <td
                                                colSpan={4}
                                                className="px-3 py-5 text-center text-muted-foreground"
                                            >
                                                {policies.length === 0
                                                    ? 'Todavía no hay políticas especiales en este tenant.'
                                                    : 'Ninguna política coincide con los filtros.'}
                                            </td>
                                        </tr>
                                    ) : (
                                        visible.map((policy) => (
                                            <tr
                                                key={policy.id}
                                                onClick={() => {
                                                    openPolicy(policy.id);
                                                }}
                                                className={cn(
                                                    'cursor-pointer align-middle transition-colors',
                                                    policy.id === editor?.policyId
                                                        ? 'bg-info/10'
                                                        : 'hover:bg-accent/40',
                                                )}
                                            >
                                                <td className="border-b border-border/50 px-2.5 py-[7px]">
                                                    <div className="font-semibold leading-tight">
                                                        {policy.name}
                                                    </div>
                                                    <div className="mt-0.5 text-[10px] leading-tight text-muted-foreground">
                                                        {policy.description.slice(0, 80)}
                                                        {policy.description.length > 80 ? '…' : ''}
                                                    </div>
                                                </td>
                                                <td className="border-b border-border/50 px-2.5 py-[7px]">
                                                    <TypeBadge type={derivePolicyType(policy)} />
                                                </td>
                                                <td className="border-b border-border/50 px-2.5 py-[7px]">
                                                    <StatusBadge status={policyView(policy)} />
                                                </td>
                                                <td className="border-b border-border/50 px-2.5 py-[7px] text-[10.5px]">
                                                    {validityLabel(policy)}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                }
                detail={
                    editor ? (
                        <PolicyDetail
                            key={editor.policyId ?? 'new'}
                            policy={selected}
                            draft={editor.draft}
                            isNew={editor.policyId === null}
                            changeCount={changeCount}
                            saving={createMutation.isPending || updateMutation.isPending}
                            canSave={
                                editor.policyId === null ? abilities.canCreate : abilities.canUpdate
                            }
                            segments={segments}
                            stores={storeCatalog.options}
                            products={productCatalog.options}
                            segmentCatalogState={segmentCatalogState}
                            storesLoading={storeCatalog.isLoading}
                            productsLoading={productCatalog.isLoading}
                            today={today}
                            logEntries={log.entries}
                            logLoading={log.isLoading}
                            onDraftChange={(draft) => {
                                setEditor((current) => (current ? { ...current, draft } : null));
                            }}
                            onSave={save}
                            onDiscard={discard}
                            onAction={setOpenAction}
                        />
                    ) : (
                        <MasterDetailEmpty
                            icon={<Shield className="size-10" aria-hidden />}
                            title="Selecciona una política"
                            sub={
                                <>
                                    Elige una fila de la izquierda para verla o editarla,
                                    <br />o crea una política nueva desde cero.
                                </>
                            }
                            action={
                                abilities.canCreate ? (
                                    <Button
                                        size="sm"
                                        className="h-6 px-2.5 text-[11px]"
                                        onClick={startNewPolicy}
                                    >
                                        + Nueva política
                                    </Button>
                                ) : null
                            }
                        />
                    )
                }
            />

            {openAction && selected ? (
                <PolicyStatusDialog
                    action={openAction}
                    policy={selected}
                    saving={statusMutation.isPending}
                    onCancel={() => {
                        setOpenAction(null);
                    }}
                    onConfirm={confirmAction}
                />
            ) : null}
        </div>
    );
}
